import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatMoney(amount: number, showSign = false): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const formatted = formatter.format(Math.abs(amount));

  if (showSign && amount !== 0) {
    return amount > 0 ? `+${formatted}` : `-${formatted}`;
  }

  return amount < 0 ? `-${formatted}` : formatted;
}

export function formatCompactMoney(amount: number): string {
  const absAmount = Math.abs(amount);

  if (absAmount >= 1_000_000) {
    return `$${(amount / 1_000_000).toFixed(1)}M`;
  }

  if (absAmount >= 1_000) {
    return `$${(amount / 1_000).toFixed(1)}K`;
  }

  return formatMoney(amount);
}

export function formatPercent(value: number, decimals = 1): string {
  return `${value >= 0 ? '+' : ''}${value.toFixed(decimals)}%`;
}

export function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });
}

export function formatRelativeDate(date: string | Date | number): string {
  const d = typeof date === 'string' ? new Date(date) : typeof date === 'number' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}

export function getMonthRange(monthOffset = 0): { start: string; end: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const end = new Date(now.getFullYear(), now.getMonth() + monthOffset + 1, 0);

  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0],
  };
}

export function getCategoryColor(category: string): string {
  const categoryLower = category.toLowerCase();

  if (categoryLower.includes('dining') || categoryLower.includes('restaurant') || categoryLower.includes('food')) {
    return 'var(--cat-dining, #f97316)';
  }
  if (categoryLower.includes('grocery') || categoryLower.includes('groceries')) {
    return 'var(--cat-groceries, #84cc16)';
  }
  if (categoryLower.includes('transport') || categoryLower.includes('gas') || categoryLower.includes('uber')) {
    return 'var(--cat-transport, #3b82f6)';
  }
  if (categoryLower.includes('entertainment') || categoryLower.includes('streaming')) {
    return 'var(--cat-entertainment, #a855f7)';
  }
  if (categoryLower.includes('utility') || categoryLower.includes('utilities') || categoryLower.includes('electric')) {
    return 'var(--cat-utilities, #06b6d4)';
  }
  if (categoryLower.includes('shopping') || categoryLower.includes('amazon')) {
    return 'var(--cat-shopping, #ec4899)';
  }
  if (categoryLower.includes('health') || categoryLower.includes('medical') || categoryLower.includes('pharmacy')) {
    return 'var(--cat-health, #14b8a6)';
  }

  return 'var(--cat-other, #6b7280)';
}

export function debounce<T extends (...args: Parameters<T>) => void>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

export function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}
